/**
 * Expenses sub-tab — studio-overhead expenses (rent, utilities,
 * marketing, etc.) AND income entries that don't belong to a
 * specific project (refund, sponsor cheque, etc.).
 *
 * Web mirror of mobile `src/features/finance/tabs/ExpensesTab.tsx`.
 * Reads via `useOrgFinances`; writes via the inline form modal.
 */
import { useMemo, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { CategorySelectWithAdd } from '@/components/CategorySelectWithAdd';
import {
  ORG_FINANCE_CATEGORIES,
  ORG_FINANCE_PAYMENT_METHODS,
  createOrgFinance,
  deleteOrgFinance,
  updateOrgFinance,
  useOrgFinances,
  type OrgFinance,
  type OrgFinanceCategory,
  type OrgFinanceKind,
  type OrgFinancePaymentMethod,
} from '@/lib/orgFinances';

function fmtInr(n: number): string {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}
function fmtDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function fromIso(s: string): Date {
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** "September 2025" — display label for the month picker. */
function monthLabel(d: Date): string {
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}
/** Move the month anchor by N months (signed). Returns a new Date
 *  pinned to the 1st-of-month at 00:00 local. */
function shiftMonth(d: Date, delta: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}
/** Inclusive start/end millis for the calendar month a Date lands in. */
function monthRangeMs(d: Date): { startMs: number; endMs: number } {
  const start = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
  return { startMs: start.getTime(), endMs: end.getTime() };
}

type FormMode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; row: OrgFinance };

export function ExpensesTab({ orgId }: { orgId: string | null }) {
  const { data, loading } = useOrgFinances(orgId);
  const [formMode, setFormMode] = useState<FormMode>({ kind: 'closed' });
  const [categoryFilter, setCategoryFilter] = useState<OrgFinanceCategory | 'all'>('all');

  // Month picker. Default to the current month; user can browse
  // backwards/forwards. KPIs + the row list both scope to this
  // month so the screen answers one question: "what happened in
  // this month?".
  const [month, setMonth] = useState<Date>(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const { startMs, endMs } = useMemo(() => monthRangeMs(month), [month]);
  const isCurrentMonth =
    month.getFullYear() === new Date().getFullYear() &&
    month.getMonth() === new Date().getMonth();

  // Month-scoped rows + category filter.
  const monthRows = useMemo(
    () =>
      data.filter((r) => {
        const ms = r.paidAt?.toMillis?.() ?? 0;
        return ms >= startMs && ms <= endMs;
      }),
    [data, startMs, endMs],
  );
  const filtered = useMemo(
    () =>
      monthRows.filter(
        (r) => categoryFilter === 'all' || r.category === categoryFilter,
      ),
    [monthRows, categoryFilter],
  );

  // Totals computed from the SELECTED month, not hardcoded MTD —
  // when browsing past months the KPIs reflect that month.
  const totals = useMemo(() => {
    let income = 0;
    let expense = 0;
    let salaries = 0;
    for (const r of monthRows) {
      if (r.kind === 'income') income += r.amount;
      else {
        expense += r.amount;
        if (r.category === 'salary') salaries += r.amount;
      }
    }
    return { income, expense, salaries, net: income - expense };
  }, [monthRows]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Month picker — prev / label / next + reset-to-today.
          Label is bold + tappable to show it's interactive. */}
      <div
        className="card"
        style={{
          padding: 12,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
          aria-label="Previous month"
        >
          <i className="ph ph-caret-left" />
        </button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            {monthLabel(month)}
          </div>
          {!isCurrentMonth ? (
            <button
              type="button"
              onClick={() =>
                setMonth(
                  new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                )
              }
              style={{
                marginTop: 2,
                background: 'transparent',
                border: 'none',
                color: 'var(--brand, #3b82f6)',
                fontSize: 11,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Jump to this month
            </button>
          ) : null}
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
          aria-label="Next month"
        >
          <i className="ph ph-caret-right" />
        </button>
      </div>

      {/* KPI strip — scoped to the SELECTED month so browsing past
          months shows that month's numbers, not always MTD. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        <Kpi label="Expense"  value={totals.expense}  tint="#EF4444" />
        <Kpi label="Salaries" value={totals.salaries} tint="#F59E0B" />
        <Kpi label="Income"   value={totals.income}   tint="#10B981" />
      </div>

      {/* Toolbar */}
      <div className="filter-bar">
        <button
          type="button"
          className={`filter-chip${categoryFilter === 'all' ? ' active' : ''}`}
          onClick={() => setCategoryFilter('all')}
        >
          All
        </button>
        {ORG_FINANCE_CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`filter-chip${categoryFilter === c.key ? ' active' : ''}`}
            onClick={() => setCategoryFilter(c.key)}
          >
            {c.label}
          </button>
        ))}
        <div className="filter-sep" />
        <button
          type="button"
          className="btn-primary"
          onClick={() => setFormMode({ kind: 'create' })}
          disabled={!orgId}
        >
          <i className="ph ph-plus" /> Add expense
        </button>
      </div>

      {/* Rows */}
      {loading ? (
        <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          monthLabel={monthLabel(month)}
          hasCategoryFilter={categoryFilter !== 'all'}
          onAdd={() => setFormMode({ kind: 'create' })}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map((row) => (
            <ExpenseRow
              key={row.id}
              row={row}
              onClick={() => setFormMode({ kind: 'edit', row })}
            />
          ))}
        </div>
      )}

      {formMode.kind !== 'closed' ? (
        <ExpenseFormModal
          orgId={orgId}
          mode={formMode}
          onClose={() => setFormMode({ kind: 'closed' })}
        />
      ) : null}
    </div>
  );
}

// ───── Sub-components ─────────────────────────────────────

function Kpi({ label, value, tint }: { label: string; value: number; tint: string }) {
  return (
    <div
      className="card"
      style={{
        padding: 12,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeft: `4px solid ${tint}`,
        borderRadius: 10,
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700 }}>
        {fmtInr(value)}
      </div>
    </div>
  );
}

function EmptyState({
  monthLabel,
  hasCategoryFilter,
  onAdd,
}: {
  monthLabel: string;
  hasCategoryFilter: boolean;
  onAdd: () => void;
}) {
  return (
    <div
      style={{
        padding: 48,
        textAlign: 'center',
        color: 'var(--text-secondary)',
      }}
    >
      <i className="ph ph-receipt" style={{ fontSize: 48, opacity: 0.5 }} />
      <div style={{ marginTop: 12, fontWeight: 600 }}>
        No entries in {monthLabel}
      </div>
      <div style={{ marginTop: 4, fontSize: 14, marginBottom: 16 }}>
        {hasCategoryFilter
          ? 'Try removing the category filter, or pick a different month.'
          : 'Track rent, utilities, software, marketing — anything not tied to a project.'}
      </div>
      <button type="button" className="btn-primary" onClick={onAdd}>
        Add expense
      </button>
    </div>
  );
}

function ExpenseRow({ row, onClick }: { row: OrgFinance; onClick: () => void }) {
  // Look up built-in label by key; fall back to raw string for
  // custom categories (their `category` is the literal label).
  const builtIn = ORG_FINANCE_CATEGORIES.find((c) => c.key === row.category);
  const catLabel = builtIn?.label ?? (row.category || '—');
  const paid = row.paidAt?.toMillis?.() ?? 0;
  const isIncome = row.kind === 'income';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: 12,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 12,
        alignItems: 'center',
        font: 'inherit',
        color: 'inherit',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {row.payee || catLabel || 'Expense'}
          </span>
          <span
            style={{
              padding: '2px 7px',
              borderRadius: 999,
              background: 'var(--surface-muted, #f1f5f9)',
              color: 'var(--text-secondary)',
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {catLabel}
          </span>
        </div>
        <div style={{ marginTop: 2, fontSize: 12, color: 'var(--text-secondary)' }}>
          {fmtDate(paid)}
          {row.paymentMethod ? ` · ${row.paymentMethod.toUpperCase()}` : ''}
          {row.note ? ` · ${row.note}` : ''}
        </div>
      </div>
      <div
        style={{
          fontWeight: 700,
          fontSize: 15,
          color: isIncome ? '#047857' : 'var(--text-primary)',
          whiteSpace: 'nowrap',
        }}
      >
        {isIncome ? '+' : '−'} {fmtInr(row.amount)}
      </div>
    </button>
  );
}

// ───── Form modal ─────────────────────────────────────────

function ExpenseFormModal({
  orgId,
  mode,
  onClose,
}: {
  orgId: string | null;
  mode: { kind: 'create' } | { kind: 'edit'; row: OrgFinance };
  onClose: () => void;
}) {
  const { user } = useAuth();
  const editing = mode.kind === 'edit' ? mode.row : null;

  const [kind, setKind] = useState<OrgFinanceKind>(editing?.kind ?? 'expense');
  const [category, setCategory] = useState<OrgFinanceCategory>(
    editing?.category ?? 'rent',
  );
  const [amountStr, setAmountStr] = useState(
    editing ? String(editing.amount) : '',
  );
  const [paidAt, setPaidAt] = useState(
    editing?.paidAt?.toMillis?.()
      ? isoDate(new Date(editing.paidAt.toMillis()))
      : isoDate(new Date()),
  );
  const [payee, setPayee] = useState(editing?.payee ?? '');
  const [paymentMethod, setPaymentMethod] = useState<OrgFinancePaymentMethod>(
    editing?.paymentMethod ?? 'bank',
  );
  const [note, setNote] = useState(editing?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const amount = parseFloat(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Amount must be greater than 0.');
      return;
    }
    if (!orgId || !user) {
      setError('Not ready — please reload.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (mode.kind === 'create') {
        await createOrgFinance({
          orgId,
          kind,
          category,
          amount,
          paidAt: fromIso(paidAt),
          payee: payee.trim() || undefined,
          paymentMethod,
          note: note.trim() || undefined,
          createdBy: user.uid,
        });
      } else {
        await updateOrgFinance(mode.row.id, {
          kind,
          category,
          amount,
          paidAt: fromIso(paidAt),
          payee: payee.trim(),
          paymentMethod,
          note: note.trim(),
        });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (mode.kind !== 'edit') return;
    if (!confirm(`Delete this ${kind}? Cannot be undone.`)) return;
    setBusy(true);
    try {
      await deleteOrgFinance(mode.row.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete.');
      setBusy(false);
    }
  }

  return (
    <>
      <div
        className="slide-overlay open"
        onClick={() => !busy && onClose()}
        style={{ background: 'rgba(15,23,42,0.45)' }}
      />
      <div className="slide-over open" style={{ width: 460 }}>
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18 }}>
            {mode.kind === 'create' ? 'Add' : 'Edit'}{' '}
            {kind === 'income' ? 'income' : 'expense'}
          </h2>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 20,
              cursor: 'pointer',
              color: 'var(--text-secondary)',
            }}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div
          style={{
            padding: 18,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            flex: 1,
          }}
        >
          {/* Kind toggle */}
          <div style={{ display: 'flex', gap: 6 }}>
            {(['expense', 'income'] as OrgFinanceKind[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`filter-chip${kind === k ? ' active' : ''}`}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                {k === 'expense' ? 'Expense' : 'Income'}
              </button>
            ))}
          </div>

          <Field label="Amount (₹) *">
            <input
              className="input"
              type="number"
              inputMode="decimal"
              value={amountStr}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setAmountStr(v);
              }}
              placeholder="0"
              autoFocus
            />
          </Field>

          <Field label="Category *">
            <CategorySelectWithAdd
              orgId={orgId ?? null}
              scope={kind === 'income' ? 'income' : 'expense'}
              builtIn={ORG_FINANCE_CATEGORIES.map((c) => ({
                value: c.key,
                label: c.label,
              }))}
              value={category}
              onChange={(v) => setCategory(v)}
              allowEmpty={false}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Date *">
              <input
                className="input"
                type="date"
                value={paidAt}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setPaidAt(v);
                }}
              />
            </Field>
            <Field label="Method">
              <select
                className="input"
                value={paymentMethod}
                onChange={(e) => {
                  const v = e.currentTarget.value as OrgFinancePaymentMethod;
                  setPaymentMethod(v);
                }}
              >
                {ORG_FINANCE_PAYMENT_METHODS.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Payee">
            <input
              className="input"
              type="text"
              value={payee}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setPayee(v);
              }}
              placeholder="Vendor / staff name"
            />
          </Field>

          <Field label="Note">
            <textarea
              className="input"
              rows={2}
              value={note}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setNote(v);
              }}
            />
          </Field>

          {error ? (
            <div
              style={{
                padding: 10,
                borderRadius: 8,
                background: '#FEE2E2',
                color: '#B91C1C',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}
        </div>

        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          {mode.kind === 'edit' ? (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              style={{
                background: 'transparent',
                border: '1px solid #fecaca',
                color: '#b91c1c',
                padding: '6px 12px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Delete
            </button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={busy}
            >
              {busy ? 'Saving…' : mode.kind === 'create' ? 'Add' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
        {label}
      </span>
      {children}
    </label>
  );
}
